import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import Card from 'react-bootstrap/Card';
import Container from 'react-bootstrap/Container';
import ListGroup from 'react-bootstrap/ListGroup';
import { BsGlobeAmericas } from 'react-icons/bs';
import { LinkContainer } from 'react-router-bootstrap';
import { useParams } from 'react-router-dom';
import { HealthcareEntityService } from '../api';
import { useDocumentTitle } from '../app/hooks/use-document-title';
import { healthcareEntityFacilityQueryKeys } from '../app/query-keys-factories/facilities';
import { createGoogleMapsLink } from '../utils/address';

const FacilityList = ({ entityId }: { entityId: string }) => {
  const LIMIT = 10;

  const [page, setPage] = useState(1);

  const {
    data: facilities,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: healthcareEntityFacilityQueryKeys.list(entityId),
    queryFn: async ({ pageParam = 0 }) =>
      await HealthcareEntityService.healthcareEntityFacilitiesList({
        limit: 10,
        offset: pageParam,
        healthcareEntity: entityId,
      }),
    getNextPageParam: (lastPage) => lastPage.next && page * LIMIT,
  });

  if (!facilities?.pages?.length || isLoading || isError) {
    return null;
  }

  const loadMore = () => {
    setPage((page) => page + 1);
    fetchNextPage();
  };

  return (
    <Card>
      <Card.Body>
        <Card.Title>
          <div className="ms-3">Facilities</div>
        </Card.Title>
        <ListGroup>
          {facilities.pages.flatMap((page) =>
            page.results!.map((facility) => (
              <LinkContainer
                key={facility.id}
                to={`/app/organization/facilities/${facility.id}`}
                style={{ cursor: 'pointer' }}
                className="border-top py-2 mx-1 hover-bg-light"
              >
                <Row className="align-items-center">
                  <Col xs={12} sm={3} className="text-section-name text-section-name--dark-brand">
                    {facility.name}
                  </Col>
                  <Col xs={12} sm={2} className="text-medium text-muted">
                    {facility.street_address}
                  </Col>
                  <Col xs={5} sm={2} className="text-medium text-muted">
                    {facility.city}
                  </Col>
                  <Col xs={4} sm={1} className="text-medium text-muted text-end">
                    {facility.state}
                  </Col>
                  <Col xs={3} sm={1} className="text-medium text-muted text-end">
                    {facility.zip_code}
                  </Col>
                  <Col xs={6} sm={2} className="text-medium text-muted">
                    {facility.emr}
                  </Col>
                  <Col xs={6} sm={1} className="p-0 text-end">
                    <a
                      href={createGoogleMapsLink(
                        facility.street_address || '',
                        facility.city,
                        facility.state,
                        facility.zip_code
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ width: 'fit-content', marginLeft: 'auto' }}
                      className="d-flex align-items-center justify-content-center btn btn-outline-primary btn-sm pt-1 text-primary-dark"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <BsGlobeAmericas className="me-2" />
                      Map
                    </a>
                  </Col>
                </Row>
              </LinkContainer>
            ))
          )}
          {hasNextPage && (
            <Button disabled={isLoading} onClick={loadMore}>
              Show More
            </Button>
          )}
        </ListGroup>
      </Card.Body>
    </Card>
  );
};

export const EntityProfile = () => {
  useDocumentTitle('Healthcare Organization');
  const { entityId } = useParams();

  const {
    data: entity,
    isLoading,
    isError,
  } = useQuery({
    queryKey: healthcareEntityFacilityQueryKeys.detail(entityId!),
    queryFn: async () =>
      await HealthcareEntityService.healthcareEntityRetrieve({
        id: entityId!,
      }),
  });

  if (!entity || isLoading || isError) {
    return null;
  }

  return (
    <div>
      <div className="bg-primary-dark py-3">
        <Container className="py-3">
          <h1>{entity.name}</h1>
        </Container>
      </div>
      <Container className="my-3 p-0">
        <FacilityList entityId={entityId!} />
      </Container>
    </div>
  );
};
